import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { FeatureNotEnabledView } from '@euler/components/entitlements/FeatureNotEnabledView';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { StoreFeature } from '@euler/model/enum';
import { StackActions, useFocusEffect } from '@react-navigation/native';
import { useCallback, useMemo, useState } from 'react';
import { Text } from 'react-native';
import { BiDashboardView } from './dashboard/BiDashboardView';
import {
  biDateChoiceOfParam,
  biDateChoiceParam,
} from './dashboard/functions/biDateRange';
import { useAnimatedAfterInstant } from './functions/useAnimatedAfterInstant';

export interface BiDashboardPageParams {
  dashboardId: string;
  /** The viewer's date range, see `biDateChoiceParam`; none: the saved. */
  range?: string;
  /** Opened in place of the BI entry: shown without a transition. */
  instant?: boolean;
}

function BiDashboard({ dashboardId, range, instant }: BiDashboardPageParams) {
  const navigation = useAppNavigation();
  useAnimatedAfterInstant(instant);
  const choice = useMemo(() => biDateChoiceOfParam(range), [range]);
  const [visits, setVisits] = useState(0);
  useFocusEffect(useCallback(() => setVisits(value => value + 1), []));
  return (
    <BiDashboardView
      dashboardId={dashboardId}
      visits={visits}
      choice={choice}
      onChoice={next =>
        navigation.setParams({ range: biDateChoiceParam(next) })
      }
      // The card's page opens on the range chosen here.
      onOpenCard={cardId =>
        navigation.navigate('BiDashboardCard', { dashboardId, cardId, range })
      }
      onBack={() => navigation.goBack()}
      onSwitch={next =>
        navigation.dispatch(
          StackActions.replace('BiDashboard', { dashboardId: next }),
        )
      }
      onManage={() => navigation.navigate('BiDashboards', {})}
      onOpenExports={() => navigation.navigate('BiExports', {})}
    />
  );
}

export const BiDashboardPage = wrapNavigatorScreen(
  ({ dashboardId, range, instant }: BiDashboardPageParams) => {
    const profile = useUserProfileInfo();
    const bi = useStoreFeature(StoreFeature.Bi);
    if (!bi.enabled) return <FeatureNotEnabledView feature={StoreFeature.Bi} />;
    if (!profile?.user.enabled)
      return <Text>当前门店尚未开通经营分析，或账号不可用。</Text>;
    return (
      <BiDashboard
        key={`${profile.org.id}:${profile.store.id}:${dashboardId}`}
        dashboardId={dashboardId}
        range={range}
        instant={instant}
      />
    );
  },
  ({ route }) => ({
    title: '数据看板',
    headerShown: false,
    headerMode: 'float',
    animationEnabled: !route.params?.instant,
  }),
);
